export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
  readonly device_name: string;
}

/** Access roles an authenticated user can carry. */
export type UserRole = 'admin' | 'analyst' | 'manager' | 'coordinator';

export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly organization_id: string;
  /** Brazilian taxpayer id, when the login payload carries it (digits or masked). */
  readonly cpf?: string | null;
  /** Profile picture URL; falls back to the initial badge when absent. */
  readonly avatar_url?: string | null;
  /** Whether the user has a face enrolled; when true, login goes through the two-step check. */
  readonly face?: boolean;
}

/** Payload returned by the refresh endpoint: a rotated access token. */
export interface RefreshTokenResult {
  readonly token: string;
  readonly token_type: string;
}

/** Payload returned by the login endpoint: the access token and the user it belongs to. */
export interface LoginResult {
  readonly token: string;
  readonly token_type: string;
  readonly user: AuthUser;
}

/**
 * Payload returned by the login endpoint when the account has a face enrolled:
 * no token and no user yet, only the challenge the face step must answer.
 */
export interface LoginChallenge {
  readonly mfa_required: true;
  readonly face?: boolean;
  readonly challenge_token: string;
}

/** Either a finished session or the challenge for the face step. */
export type LoginResponse = LoginResult | LoginChallenge;

export function isLoginChallenge(response: LoginResponse): response is LoginChallenge {
  return 'mfa_required' in response && response.mfa_required === true;
}

/** Payload returned once the face is enrolled; the same token stays valid. */
export interface FaceEnrollmentResult {
  readonly active: boolean;
  readonly face: boolean;
  readonly user: AuthUser;
}

/** Payload sent to the forgot-password endpoint to verify the account exists. */
export interface ForgotPasswordRequest {
  readonly email: string;
}

/** Payload returned when the email is linked to an existing account. */
export interface ForgotPasswordResult {
  readonly message?: string;
}

/** Payload sent to the reset-password endpoint to set a new password. */
export interface ResetPasswordRequest {
  readonly token: string;
  readonly cpf: string;
  readonly password: string;
  readonly repeatPassword: string;
}

/** Payload returned when the password is successfully reset. */
export interface ResetPasswordResult {
  readonly message?: string;
}

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error';

/** Error body returned by the API on a failed request (Laravel-style validation shape). */
export interface ApiErrorBody {
  readonly message: string;
  readonly errors?: Readonly<Record<string, readonly string[]>>;
}
